import React, { useState } from 'react';
import { getCookie } from '../utils';

export default function CreateBatch({ setBatches, fetchBatches, setActiveTab, selectedProgram, batchToEdit, selectedPoolId }) {
  const isEdit = !!batchToEdit;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- 1. THE SPLIT (LOAD LOGIC) ---
  // Reverse the math to split total ValidDays back into Years, Months, and Days
  let defY = "", defM = "", defD = "";
  
  if (isEdit) {
    // Safely grab the days (checking both cases just to be sure)
    const totalDays = batchToEdit.validDays || batchToEdit.ValidDays || 0;
    let remainingDays = parseInt(totalDays);
    
    defY = Math.floor(remainingDays / 365);
    remainingDays %= 365;
    defM = Math.floor(remainingDays / 30);
    defD = remainingDays % 30;
  }

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const daysAllowed = formData.getAll('weekdays');
    if (daysAllowed.length === 0) return alert("Please select at least one weekday.");

    setIsSubmitting(true);

    // --- 2. THE MERGE (SAVE LOGIC) ---
    // Multiply dropdown values to convert everything into a single total of days
    const y = parseInt(formData.get('validYears') || 0);
    const m = parseInt(formData.get('validMonths') || 0);
    const d = parseInt(formData.get('validDays') || 0);
    const totalValidDays = (y * 365) + (m * 30) + d;

    const apiPayload = {
      poolId: selectedPoolId,
      Title: formData.get('title'),
      Type: selectedProgram, 
      Fees: parseInt(formData.get('fees')),
      Capacity: parseInt(formData.get('capacity')), 
      TotalHours: parseInt(formData.get('hoursCount')),
      HoursPerDay: parseInt(formData.get('hoursPerDay')),
      StartTime: formData.get('startTime'),
      EndTime: formData.get('endTime'),
      ValidDays: totalValidDays, // Sending the merged integer to the database
      daysAllowed: daysAllowed,
      active: formData.get('isActive') === 'on' 
    };

    try {
      const token = getCookie('tsa_token');
      const apiHeaders = {
        'Content-Type': 'application/json',
        'x-auth-token': token || '' 
      };

      if (isEdit) {
        const batchId = batchToEdit._id || batchToEdit.id;
        const response = await fetch(`https://tsaapis.operate.live/api/batches/${batchId}`, {
          method: 'PUT',
          headers: apiHeaders,
          credentials: 'include',
          body: JSON.stringify(apiPayload)
        });

        if (!response.ok) throw new Error("Failed to update batch");
        if (fetchBatches) await fetchBatches(); 
        alert(`${selectedProgram} Batch Updated Successfully!`);

      } else {
        const response = await fetch('https://tsaapis.operate.live/api/batches', {
          method: 'POST',
          headers: apiHeaders,
          credentials: 'include',
          body: JSON.stringify(apiPayload)
        });

        if (!response.ok) throw new Error("Failed to create batch");
        if (fetchBatches) await fetchBatches(); 
        alert(`${selectedProgram} Batch Created Successfully!`);
      }
      
      setActiveTab('programs', true); 
    } catch (error) {
      console.error("API Error:", error);
      alert("Something went wrong saving the batch. Please check the console.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBatchActive = isEdit && batchToEdit.isActive !== undefined ? batchToEdit.isActive : true;

  return (
    <div className="admin-card">
      <h2>
        <span>{isEdit ? `Edit ${selectedProgram} Batch` : `Create New ${selectedProgram} Batch`}</span>
        <button className="btn-small" onClick={() => setActiveTab('programs', true)} disabled={isSubmitting}>
          <i className="fas fa-arrow-left"></i> Cancel
        </button>
      </h2>
      <form onSubmit={handleSave}>
        
        <div className="form-grid">
          
          <div className="form-group full-width" style={{ display: 'flex', gap: '15px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: 2, minWidth: '200px' }}>
              <label>Batch Title</label>
              <input type="text" name="title" defaultValue={batchToEdit?.Title || batchToEdit?.title} placeholder="e.g. Morning Advanced Warriors" required disabled={isSubmitting} />
            </div>
            
            <div style={{ flex: 1, minWidth: '100px' }}>
              <label>Capacity</label>
              <input type="number" name="capacity" defaultValue={batchToEdit?.Capacity || batchToEdit?.capacity || 15} placeholder="Max Students" required disabled={isSubmitting} />
            </div>

            <div style={{ paddingTop: '28px' }}>
              <label className="weekday-label" style={{ padding: '10px 20px', cursor: 'pointer', margin: 0, display: 'inline-flex', alignItems: 'center' }}>
                <input type="checkbox" name="isActive" key={isBatchActive ? 'active' : 'inactive'} defaultChecked={isBatchActive} disabled={isSubmitting} />
                <span style={{ marginLeft: '5px' }}>Active</span>
              </label>
            </div>
          </div>
          
          <div className="form-group">
            <label>Batch Type</label>
            <input type="text" name="type" value={selectedProgram} readOnly style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--accent-gold)', fontWeight: 'bold' }} />
          </div>
          
          <div className="form-group"><label>Fees (₹)</label><input type="number" name="fees" defaultValue={batchToEdit?.Fees || batchToEdit?.fees} required disabled={isSubmitting} /></div>
          <div className="form-group"><label>Total Hours</label><input type="number" name="hoursCount" defaultValue={batchToEdit?.TotalHours || batchToEdit?.hoursCount} required disabled={isSubmitting} /></div>
          <div className="form-group"><label>Hours Per Day</label><input type="number" name="hoursPerDay" defaultValue={batchToEdit?.HoursPerDay || batchToEdit?.hoursPerDay} required disabled={isSubmitting} /></div>
          <div className="form-group"><label>Start Time</label><input type="time" name="startTime" defaultValue={batchToEdit?.StartTime || batchToEdit?.startTime} required disabled={isSubmitting} /></div>
          <div className="form-group"><label>End Time</label><input type="time" name="endTime" defaultValue={batchToEdit?.EndTime || batchToEdit?.endTime} required disabled={isSubmitting} /></div>
          
          {/* --- 3. THE UI DROPDOWNS --- */}
          {/* These will correctly display the defY, defM, and defD values calculated at the top */}
          <div className="form-group full-width"><label>Duration Validity</label>
            <div className="flex-row">
              <select name="validYears" defaultValue={defY !== "" ? defY : ""} disabled={isSubmitting}>
                <option value="" disabled>Years</option>
                {[0,1,2,3,4,5].map(i => <option key={`y-${i}`} value={i}>{i} Years</option>)}
              </select>
              
              <select name="validMonths" defaultValue={defM !== "" ? defM : ""} disabled={isSubmitting}>
                <option value="" disabled>Months</option>
                {[...Array(12).keys()].map(i => <option key={`m-${i}`} value={i}>{i} Months</option>)}
              </select>
              
              <select name="validDays" defaultValue={defD !== "" ? defD : ""} disabled={isSubmitting}>
                <option value="" disabled>Days</option>
                {[...Array(31).keys()].map(i => <option key={`d-${i}`} value={i}>{i} Days</option>)}
              </select>
            </div>
          </div>
          
          <div className="form-group full-width"><label>Weekdays Allowed</label>
            <div className="weekdays-container">
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((day) => {
                const isChecked = isEdit && (batchToEdit.daysAllowed?.includes(day) || batchToEdit.weekdays?.includes(day));
                return (
                  <label key={day} className="weekday-label">
                    <input type="checkbox" name="weekdays" value={day} defaultChecked={isChecked} disabled={isSubmitting} /> {day}
                  </label>
                )
              })}
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            <i className={isSubmitting ? "fas fa-spinner fa-spin" : "fas fa-save"}></i> 
            {isSubmitting ? ' Saving...' : (isEdit ? ' Update Batch' : ' Save Batch')}
          </button>
        </div>
        
      </form>
    </div>
  );
}